import axios from "axios";
import { requestStepUp } from "@/lib/stepUpBus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
    baseURL: `${BACKEND_URL}/api`,
    withCredentials: true,
});

// Attempt refresh once on 401
let refreshing = null;
api.interceptors.response.use(
    (r) => r,
    async (err) => {
        const original = err.config;
        const status = err.response?.status;

        // 428 Precondition Required → step-up 2FA needed. Open the global
        // modal, wait for the user to enter a code, then retry the request.
        if (status === 428 && !original._stepUpRetry) {
            const detail = err.response?.data?.detail;
            const detailCode = typeof detail === "object" ? detail?.code : null;
            if (detailCode === "enroll_required") {
                // User has no 2FA yet — bounce them to the Security page
                if (typeof window !== "undefined" && !window.location.pathname.endsWith("/security")) {
                    window.location.assign("/security?enroll=1");
                }
                return Promise.reject(err);
            }
            if (detailCode === "step_up_required") {
                original._stepUpRetry = true;
                const ok = await requestStepUp();
                if (ok) return api(original);
                return Promise.reject(err);
            }
        }

        if (
            status === 401 &&
            !original._retry &&
            !original.url?.includes("/auth/")
        ) {
            original._retry = true;
            try {
                if (!refreshing) {
                    refreshing = api.post("/auth/refresh").finally(() => {
                        refreshing = null;
                    });
                }
                await refreshing;
                return api(original);
            } catch (refreshErr) {
                // Refresh failed — surface a debug log; the outer reject will
                // still bubble up to the caller which typically redirects to /login.
                console.warn("[api] token refresh failed", refreshErr?.message || refreshErr);
            }
        }
        return Promise.reject(err);
    },
);

export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return JSON.stringify(detail);
}

export function fmtMoney(v, currency = "INR") {
    const n = Number(v || 0);
    try {
        return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(n);
    } catch {
        return `${n.toLocaleString()}`;
    }
}

export function fmtDate(v) {
    if (!v) return "—";
    try {
        return new Date(v).toLocaleDateString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return String(v);
    }
}

export function fmtDateTime(v) {
    if (!v) return "—";
    try {
        return new Date(v).toLocaleString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return String(v);
    }
}

export function computeDueStatus(card, outstanding) {
    if (!outstanding || outstanding <= 0) return "Paid";
    if (!card?.payment_due_day) return "Not Due";
    const today = new Date();
    const day = Math.min(parseInt(card.payment_due_day, 10), 28);
    const due = new Date(today.getFullYear(), today.getMonth(), day);
    const diff = Math.round((due - today) / 86400000);
    if (diff > 7) return "Not Due";
    if (diff < 0) return "Overdue";
    if (diff === 0) return "Due Today";
    return "Due Soon";
}

export const STATUS_STYLES = {
    Active: {
        cls: "bg-blue-50 text-blue-700 border-blue-200",
    },
    Inactive: {
        cls: "bg-stone-100 text-stone-600 border-stone-200",
    },
    Closed: {
        cls: "bg-stone-100 text-stone-600 border-stone-200",
    },
    Blocked: {
        cls: "bg-red-50 text-red-700 border-red-200",
    },
    Lost: {
        cls: "bg-red-50 text-red-700 border-red-200",
    },
    Replaced: {
        cls: "bg-stone-100 text-stone-600 border-stone-200",
    },
    Paid: {
        cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    "Due Soon": {
        cls: "bg-amber-50 text-amber-700 border-amber-200",
    },
    "Due Today": {
        cls: "bg-amber-50 text-amber-700 border-amber-200",
    },
    Overdue: {
        cls: "bg-red-50 text-red-700 border-red-200",
    },
    "Not Due": {
        cls: "bg-stone-50 text-stone-600 border-stone-200",
    },
    "Partially Paid": {
        cls: "bg-amber-50 text-amber-700 border-amber-200",
    },
};

export const CATEGORIES = [
    "Travel",
    "Hotel",
    "Food & Dining",
    "Fuel",
    "Office Expense",
    "IT / Software",
    "Subscription",
    "Shopping",
    "Utility Bills",
    "Vendor Payment",
    "Client Expense",
    "Personal Expense",
    "Medical",
    "Insurance",
    "Education",
    "Repair & Maintenance",
    "EMI",
    "Tax / Government Payment",
    "Other",
];

export const CARD_NETWORKS = ["Visa", "Mastercard", "RuPay", "Amex", "Diners", "JCB", "Discover", "UnionPay"];
// Card Purpose: how the card is *used* (kept the field name `card_type` in the
// DB for backward-compat, but labelled "Purpose" in the UI).
export const CARD_TYPES = [
    "Personal", "Business", "Family", "Corporate",
    "Travel", "Fuel", "Subscription Only", "Emergency Backup",
    "Add-on Card",
];
// New: what *kind* of card this is (Credit / Debit / Charge / etc.)
export const CARD_KINDS = [
    "Credit Card", "Debit Card", "Charge Card", "Forex Card",
    "Prepaid Card", "Virtual Card", "Corporate Card", "Fuel Card",
    "Business Credit Card", "BNPL",
];
// New: level / variant — for benefits comparison.
export const CARD_LEVELS = [
    "Classic", "Silver", "Gold", "Platinum", "Titanium",
    "Signature", "Infinite", "World", "World Elite",
    "Select", "Privilege", "Premium", "Black", "Reserve", "Centurion",
];
export const CARD_STATUSES = [
    "Active",
    "Inactive",
    "Closed",
    "Blocked",
    "Lost",
    "Replaced",
];
export const CARD_CATEGORIES = [
    "Premium",
    "Travel",
    "Fuel",
    "Cashback",
    "Business",
    "Utility",
    "Shopping",
    "General",
];
export const PAYMENT_MODES = [
    "Bank Transfer",
    "UPI",
    "Auto Debit",
    "Cheque",
    "Cash Deposit",
    "CRED",
    "Amazon Pay",
    "Other Wallet",
    "Other",
];
export const EXPENSE_PAYMENT_MODES = [
    "Card Swipe",
    "Online",
    "Subscription",
    "EMI",
    "International",
    "Wallet",
    "Amazon Pay",
    "CRED",
    "Other",
];
export const PLATFORMS = [
    "Amazon",
    "Flipkart",
    "CRED",
    "Myntra",
    "Swiggy",
    "Zomato",
    "BigBasket",
    "Zepto",
    "Blinkit",
    "Paytm",
    "PhonePe",
    "Google Pay",
    "Amazon Pay",
    "Direct Merchant Website",
    "Offline Store",
    "Other",
];

/**
 * Merchant → suggested fields. Keys are lowercase substring patterns; the
 * first matching pattern wins. Kept small and boring on purpose — expand
 * this table as your own spend patterns emerge.
 */
export const MERCHANT_HINTS = [
    { match: /\b(swiggy|swigy)\b/i,   platform: "Swiggy",    category: "Food & Dining", payment_mode: "Online",       business_personal: "Personal" },
    { match: /\bzomato\b/i,           platform: "Zomato",    category: "Food & Dining", payment_mode: "Online",       business_personal: "Personal" },
    { match: /\bblinkit\b/i,          platform: "Blinkit",   category: "Food & Dining", payment_mode: "Online",       business_personal: "Personal" },
    { match: /\b(zepto)\b/i,          platform: "Zepto",     category: "Food & Dining", payment_mode: "Online",       business_personal: "Personal" },
    { match: /\b(big ?basket|bb )\b/i,platform: "BigBasket", category: "Food & Dining", payment_mode: "Online",       business_personal: "Personal" },
    { match: /\b(amazon(?! pay)|amzn)\b/i, platform: "Amazon",   category: "Shopping",  payment_mode: "Online",       business_personal: "Personal" },
    { match: /\bamazon pay\b/i,       platform: "Amazon Pay",category: "Shopping",      payment_mode: "Amazon Pay",   business_personal: "Personal" },
    { match: /\bflipkart\b/i,         platform: "Flipkart",  category: "Shopping",      payment_mode: "Online",       business_personal: "Personal" },
    { match: /\bmyntra\b/i,           platform: "Myntra",    category: "Shopping",      payment_mode: "Online",       business_personal: "Personal" },
    { match: /\b(cred)\b/i,           platform: "CRED",      category: "Utility Bills", payment_mode: "CRED",         business_personal: "Personal" },
    { match: /\b(uber|ola|rapido)\b/i,platform: "Direct Merchant Website", category: "Travel", payment_mode: "Online", business_personal: "Personal" },
    { match: /\b(indigo|indi ?go|air ?india|vistara|akasa)\b/i, platform: "Direct Merchant Website", category: "Travel", payment_mode: "Online", business_personal: "Business" },
    { match: /\b(bookmyshow|paytm insider)\b/i, platform: "Paytm", category: "Shopping", payment_mode: "Online",      business_personal: "Personal" },
    { match: /\b(bpcl|hpcl|iocl|indian ?oil|shell|petrol|fuel)\b/i, platform: "Offline Store", category: "Fuel",       payment_mode: "Card Swipe", business_personal: "Business" },
    { match: /\b(apollo|1mg|pharmeasy|netmeds|medplus)\b/i, platform: "Direct Merchant Website", category: "Medical", payment_mode: "Online", business_personal: "Personal" },
    { match: /\b(netflix|prime|hotstar|zee5|sonyliv|spotify|youtube ?premium)\b/i, platform: "Direct Merchant Website", category: "Subscription", payment_mode: "Subscription", business_personal: "Personal" },
    { match: /\b(aws|amazon web services|gcp|azure|digitalocean|linode)\b/i, platform: "Direct Merchant Website", category: "IT / Software", payment_mode: "Subscription", business_personal: "Business" },
    { match: /\b(google (workspace|domains)|godaddy|namecheap|cloudflare)\b/i, platform: "Direct Merchant Website", category: "IT / Software", payment_mode: "Subscription", business_personal: "Business" },
    { match: /\b(zerodha|groww|kite|upstox)\b/i, platform: "Direct Merchant Website", category: "Other",             payment_mode: "Online",      business_personal: "Personal" },
];

export function suggestFromMerchant(merchant) {
    if (!merchant) return null;
    const m = String(merchant).trim();
    if (m.length < 3) return null;
    const hit = MERCHANT_HINTS.find((h) => h.match.test(m));
    return hit || null;
}
export const ROLES = [
    { value: "admin", label: "Owner / Admin" },
    { value: "finance", label: "Finance Manager" },
    { value: "expense_user", label: "Expense Entry User" },
    { value: "viewer", label: "Viewer" },
];
